import { useContext, useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { Text, Paper, TextInput, Button, Container } from "@mantine/core";
import { useForm } from "@mantine/form";

import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  trimString,
  validateEmail,
  validateGmail,
} from "../../Lib/Methods";
import { AppName, BrandImages, Colors } from "../../Lib/Data";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { Endpoints } from "../../Lib/Endpoints";
import { NonPaginatedResponse } from "../../Lib/Responses";
import { SubscriptionFormProps, TempProps } from "../../Lib/Types";

import "./styles.scss";
import MegaLoader from "../../Components/MegaLoader";
import { AppContext } from "../Wrapper";

export default function RegisterYoutube() {
  const params = useParams();
  const context = useContext(AppContext);

  const theme = context?.theme ?? "light";

  document.title = `Confirm YouTube Email | ${AppName}`;

  const navigate = useNavigate();
  const { tempID } = params;

  const {
    data: formData,
    isLoading: isLoadingForm,
    response,
  } = usePerformRequest<
    NonPaginatedResponse<{ temp: TempProps; form: SubscriptionFormProps }>
  >({
    method: "GET",
    route: Endpoints.GetSingleForm(tempID as string),
  });

  const form = useForm({
    initialValues: {
      email: "",
    },
    validate: {
      email: (value) =>
        validateEmail(value, true)
          ? null
          : "Please enter a valid GMAIL address!",
    },
  });

  useEffect(() => {
    if (response && response.status) {
      if (response.status === 404) {
        DefaultErrorNotification("This record does not exist!");
        navigate(-1);
      } else if (response.status === 401) {
        DefaultErrorNotification("You are not authorized to access this page!");
        navigate(-1);
      }
    }
  }, [isLoadingForm]);

  useEffect(() => {
    if (formData && formData.data && formData.data?.form?.id?.length > 0) {
      form.setFieldValue("email", formData?.data?.form?.email);
    }
  }, [formData]);

  const [isLoading, setLoading] = useState<boolean>(false);
  const AddToYoutube = async () => {
    const { hasErrors } = form.validate();
    if (!hasErrors) {
      const { email } = form.values;
      setLoading(true);
      const r = await PerformRequest({
        method: "POST",
        route: Endpoints.GetUpdateYoutubeForm(tempID as string),
        data: {
          email: trimString(email, true),
        },
        callback: () => {
          setLoading(false);
        },
      });
      setLoading(false);
      if (r) {
        const { status } = r;
        if ([200, 201].includes(status)) {
          DefaultSuccessNotification("Form has been updated");
          navigate("/d/transactions");
          context?.reloadUnregistered();
        } else {
          DefaultErrorNotification(
            "Could not update form! Please contact support"
          );
        }
      } else {
        DefaultErrorNotification(
          "Could not update form! Please contact support"
        );
      }
    }
  };
  return (
    <Container size="sm" className="register-youtube-container">
      {isLoadingForm ? (
        <MegaLoader />
      ) : (
        <Paper withBorder className={`paper paper-${theme}`}>
          <Text
            fw={700}
            fz={24}
            c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
            mb="xs"
          >
            YouTube Premium Registration
          </Text>
          <Text c="dimmed" fz="sm" mb="md">
            Please provide the email to be added to YouTube Premium
          </Text>
          <img
            src={BrandImages.retrieveImage("YouTube Premium")}
            alt=""
            className="image"
          />

          <div className="flex-col width-100">
            <TextInput
              disabled={isLoadingForm}
              label="Email"
              name="email"
              type="email"
              placeholder="you@gmail.com"
              description="Enter a valid Gmail address"
              {...form.getInputProps("email")}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  AddToYoutube();
                }
              }}
              size="md"
              w="100%"
            />
            <Button
              color={Colors.GreenPrimary}
              loading={isLoading}
              onClick={AddToYoutube}
              mt="xl"
              disabled={!validateGmail(form.values.email)}
              size="md"
              fullWidth
            >
              Submit
            </Button>
          </div>
        </Paper>
      )}
    </Container>
  );
}
